/**
 * Client-side video duration determination for video events that don't carry
 * a `duration` value in their tags.
 *
 * - Direct media files (mp4, webm, …): a hidden <video preload="metadata"> reads
 *   the duration from the file headers — only a few KB are transferred.
 * - HLS streams (m3u8): the playlist is fetched and EXTINF durations are summed.
 *   Master playlists (which point to other m3u8 playlists) are resolved one level
 *   deep by fetching the first variant playlist.
 * - YouTube links and unreachable media: duration is not determinable.
 *
 * Results are cached in a module-level map so each URL is only probed once.
 */

import { isHlsUrl, isHlsMimeType, isYouTubeUrl } from '@/lib/videoTypes';

export type DurationProbeResult =
  | { status: 'ok'; duration: number }
  | { status: 'unknown' };

const cache = new Map<string, DurationProbeResult>();
const inflight = new Map<string, Promise<DurationProbeResult>>();

/** CORS proxy for cross-origin playlist fetches. */
const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';

async function fetchText(url: string, timeoutMs: number): Promise<string> {
  const doFetch = async (target: string) => {
    const res = await fetch(target, { signal: AbortSignal.timeout(timeoutMs) });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.text();
  };
  try {
    return await doFetch(url);
  } catch (err) {
    // Retry through the CORS proxy on network/CORS failures
    if (err instanceof Error && err.name === 'AbortError') throw err;
    return doFetch(CORS_PROXY + encodeURIComponent(url));
  }
}

/** Parse an m3u8 playlist. Returns total duration for media playlists, or the
 * first variant playlist URL for master playlists. */
function parseM3U8(text: string, baseUrl: string): { duration?: number; variantUrl?: string } {
  const lines = text.split('\n').map(l => l.trim());

  const isMaster = lines.some(l => l.startsWith('#EXT-X-STREAM-INF'));
  if (isMaster) {
    // Find the first URI line after an #EXT-X-STREAM-INF
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].startsWith('#EXT-X-STREAM-INF')) {
        const uri = lines.slice(i + 1).find(l => l && !l.startsWith('#'));
        if (uri) {
          return { variantUrl: new URL(uri, baseUrl).href };
        }
      }
    }
    return {};
  }

  // Media playlist: sum EXTINF durations
  let total = 0;
  let found = false;
  for (const line of lines) {
    if (line.startsWith('#EXTINF:')) {
      const value = parseFloat(line.slice('#EXTINF:'.length));
      if (Number.isFinite(value)) {
        total += value;
        found = true;
      }
    }
  }
  return found && total > 0 ? { duration: total } : {};
}

async function probeHlsDuration(url: string, depth = 0): Promise<DurationProbeResult> {
  if (depth > 3) return { status: 'unknown' };
  try {
    const text = await fetchText(url, 8000);
    if (!text.includes('#EXTM3U')) return { status: 'unknown' };
    const parsed = parseM3U8(text, url);
    if (parsed.duration) return { status: 'ok', duration: parsed.duration };
    if (parsed.variantUrl) return probeHlsDuration(parsed.variantUrl, depth + 1);
    return { status: 'unknown' };
  } catch {
    return { status: 'unknown' };
  }
}

function probeFileDuration(url: string): Promise<DurationProbeResult> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;

    const timeout = setTimeout(() => {
      cleanup();
      resolve({ status: 'unknown' });
    }, 8000);

    const cleanup = () => {
      clearTimeout(timeout);
      video.removeAttribute('src');
      video.load();
    };

    video.addEventListener('loadedmetadata', () => {
      const d = video.duration;
      cleanup();
      if (Number.isFinite(d) && d > 0 && d !== Infinity) {
        resolve({ status: 'ok', duration: d });
      } else {
        resolve({ status: 'unknown' });
      }
    }, { once: true });

    video.addEventListener('error', () => {
      cleanup();
      resolve({ status: 'unknown' });
    }, { once: true });

    video.src = url;
  });
}

/**
 * Probe the duration of a video URL. Cached — subsequent calls for the same URL
 * return immediately. Never throws.
 */
export function probeVideoDuration(url: string, mimeType?: string): Promise<DurationProbeResult> {
  const cached = cache.get(url);
  if (cached) return Promise.resolve(cached);

  const pending = inflight.get(url);
  if (pending) return pending;

  let promise: Promise<DurationProbeResult>;

  if (isYouTubeUrl(url)) {
    // YouTube durations can't be determined client-side without an API key
    promise = Promise.resolve({ status: 'unknown' } as const);
  } else if (isHlsUrl(url) || isHlsMimeType(mimeType)) {
    promise = probeHlsDuration(url);
  } else {
    promise = probeFileDuration(url);
  }

  const tracked = promise.then((result) => {
    cache.set(url, result);
    inflight.delete(url);
    return result;
  }).catch(() => {
    const result: DurationProbeResult = { status: 'unknown' };
    cache.set(url, result);
    inflight.delete(url);
    return result;
  });

  inflight.set(url, tracked);
  return tracked;
}
